import React, {useEffect, useState} from 'react';
import {Card, CardContent, CardHeader, Grid, Typography} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import RecentActivityGrid from "./RecentActivityGrid";
import {useClient} from "../core/client";
import DashboardStatCard from "./DashboardStatCard";

const Dashboard = () => {
    const client = useClient();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalDefendants: 0,
        activeCases: 0,
        missedCheckins: 0,
        missedToday: 0,
        completedToday: 0,
        todayTotalCheckins: 0,
        defendantsAddedToday: 0,
        missedTrend7d: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await client.get('/defendants/stats');
                if (isMounted) {
                    setStats({
                        totalDefendants: data.totalDefendants ?? 0,
                        activeCases: data.activeCases ?? 0,
                        missedCheckins: data.missedCheckins ?? 0,
                        missedToday: data.missedToday ?? 0,
                        completedToday: data.completedToday ?? 0,
                        todayTotalCheckins: data.todayTotalCheckins ?? 0,
                        defendantsAddedToday: data.defendantsAddedToday ?? 0,
                        missedTrend7d: Array.isArray(data.missedTrend7d) ? data.missedTrend7d : [],
                    });
                }
            } catch (err) {
                console.error('Failed to fetch dashboard stats', err);
                if (isMounted) {
                    setError('Unable to load stats');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchStats();
        return () => {
            isMounted = false;
        };
    }, [client]);

    const totalDefendants = loading ? '—' : stats.totalDefendants;
    const missedCheckins = loading ? '—' : stats.missedCheckins;
    const completedToday = loading ? '—' : stats.completedToday;
    const completedTodayStatus = loading
        ? 'Calculating completion target...'
        : `Completed ${stats.completedToday} of ${stats.todayTotalCheckins} scheduled today`;
    const inactiveCases = Math.max(stats.totalDefendants - stats.activeCases, 0);
    const activeRate = stats.totalDefendants > 0
        ? Math.round((stats.activeCases / stats.totalDefendants) * 100)
        : 50;
    const completedRate = stats.todayTotalCheckins > 0
        ? Math.min(100, Math.round((stats.completedToday / stats.todayTotalCheckins) * 100))
        : 0;

    return (
        <div style={{ padding: '20px' }}>
            <Grid container spacing={2}>
                {error && (
                    <Grid item xs={12}>
                        <Typography color="error">{error}</Typography>
                    </Grid>
                )}
                <Grid item xs={12} md={4}>
                    <DashboardStatCard
                        title="Total Defendants"
                        value={totalDefendants}
                        change={loading ? 0 : stats.defendantsAddedToday}
                        status={loading ? 'Loading active/inactive counts...' : `Active: ${stats.activeCases} | Inactive: ${inactiveCases}`}
                        icon={<PeopleIcon />}
                        progress={activeRate}
                        color="#14b8a6"
                        onClick={() => navigate('/dashboard/total-defendants')}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <DashboardStatCard
                        title="Missed Check-ins"
                        value={missedCheckins}
                        change={loading ? 0 : -stats.missedToday}
                        status="Lower is better"
                        icon={<WarningIcon />}
                        trendData={stats.missedTrend7d}
                        color="#f59e0b"
                        onClick={() => navigate('/dashboard/missed-checkins')}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <DashboardStatCard
                        title="Completed Today"
                        value={completedToday}
                        change={loading ? 0 : stats.completedToday}
                        status={completedTodayStatus}
                        icon={<CheckCircleIcon />}
                        progress={completedRate}
                        gaugeValue={stats.completedToday}
                        gaugeTotal={stats.todayTotalCheckins}
                        color="#22c55e"
                        onClick={() => navigate('/dashboard/completed-today')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title='Recent Activity'
                        />
                        <CardContent>
                            <RecentActivityGrid />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
};

export default Dashboard;
